import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import { arcPath } from '../../utils/charts/svg-utils.ts';
import { DEFAULT_CHART_COLORS } from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import { formatNumber } from '../../utils/charts/chart-layout.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import type { ChartDataPoint, ChartOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// PieChart – pure SVG pie chart (full circle, no inner radius)
// ---------------------------------------------------------------------------

interface PieChartSignature {
  Args: ChartOptions & {
    /** Chart data points */
    data: ChartDataPoint[];
    /** Optional value formatter (currency/divisor/decimals); defaults to formatNumber. */
    formatValue?: (value: number) => string;
    /** Bounded tile height (px). When set, enables scale-to-fit (see fit mode). */
    height?: number;
  };
  Element: HTMLDivElement;
}

interface PieSegment {
  path: string;
  color: string;
  data: ChartDataPoint;
  percent: number;
  valueLabel: string;
}

const MIN_SIZE = 160;
// Fit mode (opt-in when a bounded @height is supplied, e.g. a dashboard tile):
// draw in a fixed coordinate space and let the SVG scale into the measured box,
// so the pie shrinks to fit instead of clipping — center text and stroke scale
// with it. Consumers that don't pass @height keep the legacy square sizing.
const FIT_VIEW = 300;
const MIN_FIT = 44;

export default class PieChart extends Component<PieChartSignature> {
  @tracked containerWidth = 300;
  @tracked containerHeight = 300;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get rawTotal() {
    return this.args.data.reduce((sum, d) => sum + d.value, 0);
  }
  // Clamped to ≥1 only so segment fractions never divide by zero; display uses rawTotal.
  get total() {
    return this.rawTotal || 1;
  }

  get isFit() {
    return this.args.height != null;
  }

  /** Legacy square size (fit mode ignores this in favor of the fixed view). */
  get size() {
    const usable = Math.min(this.containerWidth, this.containerHeight);
    return Math.max(MIN_SIZE, usable);
  }

  /** Rendered SVG px. Fit mode fits the measured box; legacy uses `size`. */
  get renderPx() {
    if (!this.isFit) return this.size;
    return Math.max(MIN_FIT, Math.min(this.containerWidth, this.containerHeight));
  }

  /** Drawing coordinate space — fixed in fit mode so the vector scales. */
  get coordSpace() {
    return this.isFit ? FIT_VIEW : this.size;
  }

  get outerRadius() {
    return (this.coordSpace - 4) / 2;
  }

  get cx() {
    return this.coordSpace / 2;
  }

  get cy() {
    return this.coordSpace / 2;
  }

  get viewBox() {
    return `0 0 ${this.coordSpace} ${this.coordSpace}`;
  }

  get segments(): PieSegment[] {
    const { data } = this.args;
    if (!data.length) return [];

    let currentAngle = 0;
    return data.map((d, i) => {
      const fraction = d.value / this.total;
      const startAngle = currentAngle;
      const endAngle = currentAngle + fraction * Math.PI * 2 - 0.005;
      currentAngle += fraction * Math.PI * 2;

      return {
        path: arcPath({
          cx: this.cx,
          cy: this.cy,
          outerRadius: this.outerRadius,
          innerRadius: 0,
          startAngle,
          endAngle: Math.max(endAngle, startAngle + 0.01),
        }),
        color: this.colors[i % this.colors.length]!,
        data: d,
        percent: Math.round(fraction * 100),
        valueLabel: (this.args.formatValue ?? formatNumber)(d.value),
      };
    });
  }

  /** The hovered slice — drives the in-chart label (donut shows this in its hole). */
  get hoveredSegment(): PieSegment | null {
    if (this.hoveredIndex === null) return null;
    return this.segments[this.hoveredIndex] ?? null;
  }
  get hoveredValueLabel() {
    return this.hoveredSegment?.valueLabel ?? '';
  }

  get formattedTotal() {
    return (this.args.formatValue ?? formatNumber)(this.rawTotal);
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get legendPosition() {
    return this.args.legendPosition ?? 'bottom';
  }

  get isSideLegend() {
    return this.legendPosition === 'left' || this.legendPosition === 'right';
  }

  // Flex direction places the legend on the requested side; default 'bottom'
  // reproduces the original column layout.
  get rootClass() {
    let base: string;
    switch (this.legendPosition) {
      case 'top':
        base = 'flex flex-col-reverse items-center gap-4';
        break;
      case 'left':
        base = 'flex flex-row-reverse items-center justify-center gap-4';
        break;
      case 'right':
        base = 'flex flex-row items-center justify-center gap-4';
        break;
      default:
        base = 'flex flex-col items-center gap-4';
    }
    // Fit mode fills the given box so the chart area (flex-1) can shrink to fit.
    return this.isFit ? `${base} h-full min-h-0` : base;
  }

  get chartWrapperClass() {
    if (this.isSideLegend) return 'tio-flex-center relative flex-1 min-h-0';
    return this.isFit
      ? 'tio-flex-center relative w-full flex-1 min-h-0'
      : 'tio-flex-center relative w-full';
  }

  get legendClass() {
    return this.isSideLegend
      ? 'flex flex-col gap-2 text-sm shrink-0'
      : 'grid grid-cols-2 gap-x-4 gap-y-2 text-sm';
  }

  get legendItems() {
    return this.args.data.map((d, i) => ({
      label: d.category,
      color: this.colors[i % this.colors.length]!,
      value: d.value,
      percent: Math.round((d.value / this.total) * 100),
    }));
  }

  installObserver = modifier((element: HTMLElement) => {
    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      // Store the true height; each mode applies its own floor (legacy `size`
      // still floors at MIN_SIZE, so non-fit consumers are unchanged).
      this.containerHeight = entry.contentRect.height || this.containerHeight;
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 300;
    this.containerHeight = element.clientHeight || this.containerHeight;

    return () => observer.disconnect();
  });

  handleSegmentClick = (segment: PieSegment) => {
    this.args.onClick?.(segment.data);
  };

  setHovered = (index: number) => {
    this.hoveredIndex = index;
  };

  clearHovered = () => {
    this.hoveredIndex = null;
  };

  trackPointer = (event: MouseEvent) => {
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 8;
  };

  // Keyboard focus carries no cursor coords — anchor the tooltip to the focused
  // segment's box so it doesn't render at (0,0).
  trackFocus = (event: FocusEvent) => {
    if (!(event.target instanceof Element)) return;
    const rect = event.target.getBoundingClientRect();
    this.tooltipX = rect.left + rect.width / 2;
    this.tooltipY = rect.top;
  };

  handleSegmentKeydown = (segment: PieSegment, event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSegmentClick(segment);
    }
  };

  <template>
    <div data-test-chart data-test-chart-type="pie" class={{this.rootClass}} ...attributes>
      <div class={{this.chartWrapperClass}} {{this.installObserver}}>
        <svg
          role="img"
          aria-label="Pie chart"
          {{style width=(concat this.renderPx "px") height=(concat this.renderPx "px")}}
          viewBox={{this.viewBox}}
          preserveAspectRatio="xMidYMid meet"
          class="overflow-visible"
        >
          {{#each this.segments as |seg i|}}
            <path
              data-test-chart-segment
              d={{seg.path}}
              fill={{seg.color}}
              class="transition-transform duration-150 origin-center cursor-pointer
                {{if (isEqual this.hoveredIndex i) 'tio-chart-segment--active'}}"
              role="button"
              tabindex="0"
              aria-label="{{seg.data.category}}: {{seg.valueLabel}} ({{seg.percent}}%)"
              {{on "mouseenter" (fn this.setHovered i)}}
              {{on "mousemove" this.trackPointer}}
              {{on "mouseleave" this.clearHovered}}
              {{on "focus" (fn this.setHovered i)}}
              {{on "focus" this.trackFocus}}
              {{on "blur" this.clearHovered}}
              {{on "click" (fn this.handleSegmentClick seg)}}
              {{on "keydown" (fn this.handleSegmentKeydown seg)}}
            />
          {{/each}}

          {{! Center total — persistent; outlined (see .tio-pie-center-*) so the
              number pops over the slice colors, since a full pie has no hole to
              sit it in (unlike the donut). Per-slice detail is in the tooltip. }}
          <text
            x={{this.cx}}
            y={{this.cy}}
            text-anchor="middle"
            dominant-baseline="central"
            class="tio-pie-center-value"
          >{{this.formattedTotal}}</text>
          <text
            x={{this.cx}}
            y={{add this.cy 20}}
            text-anchor="middle"
            dominant-baseline="central"
            class="tio-pie-center-label"
          >Total</text>
        </svg>
      </div>

      {{! Floating tooltip anchored to the cursor — consistent with the bar/line/area charts. }}
      {{#if this.hoveredSegment}}
        <div
          data-test-chart-tooltip
          class="tio-chart-tooltip -translate-x-1/2 -translate-y-full"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium">{{this.hoveredSegment.data.category}}</div>
          <div class="tio-text-muted">{{this.hoveredValueLabel}}
            ({{this.hoveredSegment.percent}}%)</div>
        </div>
      {{/if}}

      {{#if this.showLegend}}
        <div data-test-chart-legend class={{this.legendClass}}>
          {{#each this.legendItems as |item|}}
            <div class="tio-row-snug">
              <span
                class="tio-chart-dot-swatch tio-chart-dot-swatch--lg"
                {{style color=item.color}}
              ></span>
              <span class="tio-text-default font-medium truncate">{{item.label}}</span>
              <span class="tio-text-muted shrink-0">({{item.percent}}%)</span>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

function add(a: number, b: number) {
  return a + b;
}

export type { PieChartSignature };
